import { useEffect, useRef } from 'react';
import type { CanvasState } from './useCanvasTransform';
import type { CanvasObject } from '../types';
import type { AABB } from '../utils/aabb';
import { getHandles, hitTestHandle } from '../utils/handles';
import { computeAABB, computeRotatedAABB } from '../utils/aabb';
import { getClickedArtboard } from '../utils/artboardUtils';

export function useMoveSelected(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  getCanvasState: () => CanvasState,
  getActiveTool: () => 'select' | 'rectangle' | 'circle' | 'text' | 'artboard',
  selectedId: string | null,
  getObjectById: (id: string | null) => CanvasObject | null,
  hitTestPoint: (x: number, y: number) => string | null,
  moveSelectedBy: (dx: number, dy: number, targetIds?: string[]) => void,
  requestRender: () => void,
  getBbox: () => AABB | null,
  setSelectedId: (id: string | null) => void,
  shouldBlock?: () => boolean,
  isDraggingRef?: React.MutableRefObject<boolean>,
  onWorkComplete?: () => void,
  // 다중 선택 관련 파라미터
  selectedIds?: Set<string>,
  addToSelection?: (id: string) => void,
  removeFromSelection?: (id: string) => void,
  setSelection?: (ids: string[]) => void,
  clearSelection?: () => void,
  getSelectedObjects?: () => CanvasObject[],
  getObjectsArray?: () => CanvasObject[],
  setDragSelectRect?: (rect: { x: number; y: number; width: number; height: number } | null) => void,
  // 텍스트 툴 관련 파라미터
  onTextToolClick?: (x: number, y: number, editingId?: string) => void,
  // 아트보드 툴 관련 파라미터
  artboards?: any[],
  onArtboardClick?: (artboardId: string | null) => void
) {
  const draggingRef = useRef(false);
  const startPtRef = useRef<{ x: number; y: number } | null>(null);
  const lastPtRef = useRef<{ x: number; y: number } | null>(null);
  const draggingTargetIdRef = useRef<string | null>(null);
  const dragSelectingRef = useRef(false);
  const dragSelectStartRef = useRef<{ x: number; y: number } | null>(null);
  // 드래그 시작 시점의 객체 원본 좌표 (축스냅 기준점)
  const initialObjectPosRef = useRef<{ x: number; y: number } | null>(null);
  // 이전 프레임의 축스냅 적용된 객체 위치
  const lastObjectPosRef = useRef<{ x: number; y: number } | null>(null);
  // 드래그 시작 임계값 관리 (화면 좌표 기준)
  const dragStartedRef = useRef(false);
  const dragStartClientRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const toWorld = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      const cx = clientX - rect.left;
      const cy = clientY - rect.top;
      const { scale, offset } = getCanvasState();
      return { x: (cx - offset.x) / scale, y: (cy - offset.y) / scale };
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const tool = getActiveTool();
      if (tool !== 'select' && tool !== 'text' && tool !== 'artboard') return;
      if (shouldBlock && shouldBlock()) return;
      
      const p = toWorld(e.clientX, e.clientY);
      
      // 아트보드 툴: 아트보드 선택
      if (tool === 'artboard' && artboards && onArtboardClick) {
        const clickedArtboardId = getClickedArtboard(p.x, p.y, artboards);
        onArtboardClick(clickedArtboardId);
        return;
      }
      
      const hitId = hitTestPoint(p.x, p.y);
      
      // 다중 선택 모드: AABB 히트 테스트
      const isMultiSelect = selectedIds && selectedIds.size > 1;
      let isInMultiSelectAABB = false;
      
      if (isMultiSelect && getSelectedObjects) {
        const selectedObjs = getSelectedObjects();
        // 다중 선택 시 회전된 객체의 실제 AABB 포함
        const aabb = computeRotatedAABB(selectedObjs);
        if (aabb) {
          isInMultiSelectAABB = p.x >= aabb.x && p.x <= aabb.x + aabb.width &&
                                p.y >= aabb.y && p.y <= aabb.y + aabb.height;
          
          // 다중 선택 바운딩 박스의 핸들 체크
          if (isInMultiSelectAABB) {
            const canvasEl = canvasRef.current;
            if (canvasEl) {
              const rect = canvasEl.getBoundingClientRect();
              const screenX = e.clientX - rect.left;
              const screenY = e.clientY - rect.top;
              const { scale, offset } = getCanvasState();
              const handles = getHandles(aabb, scale);
              // 다중 선택은 회전 0도
              const hitHandle = hitTestHandle(screenX, screenY, handles, scale, offset, 0, aabb.x + aabb.width / 2, aabb.y + aabb.height / 2);
              if (hitHandle) return; // 핸들을 클릭했으면 이동하지 않음
            }
          }
        }
      } else {
        // 단일 선택 모드: 기존 로직
        const bbox = getBbox();
        if (bbox) {
          const canvasEl = canvasRef.current;
          if (canvasEl) {
            const rect = canvasEl.getBoundingClientRect();
            const screenX = e.clientX - rect.left;
            const screenY = e.clientY - rect.top;
            const { scale, offset } = getCanvasState();
            const handles = getHandles(bbox, scale);
            // 회전 각도와 중심 좌표 가져오기
            const obj = getObjectById(selectedId);
            const rotation = obj?.rotation || 0;
            const centerX = obj ? obj.x + obj.width / 2 : undefined;
            const centerY = obj ? obj.y + obj.height / 2 : undefined;
            const hitHandle = hitTestHandle(screenX, screenY, handles, scale, offset, rotation, centerX, centerY);
            if (hitHandle) return; // 핸들을 클릭했으면 이동하지 않음
          }
        }
      }
      
      // Shift 키: 다중 선택 토글
      if (e.shiftKey && hitId) {
        if (selectedIds && selectedIds.has(hitId)) {
          // 이미 선택된 객체면 제거
          removeFromSelection?.(hitId);
        } else {
          // 선택되지 않은 객체면 추가
          // 단일 선택 모드에서 다중 선택으로 전환 시 selectedId도 포함
          if (selectedId && (!selectedIds || selectedIds.size === 0)) {
            // selectedId를 먼저 selectedIds에 추가
            setSelection?.([selectedId, hitId]);
          } else {
            addToSelection?.(hitId);
          }
        }
        return;
      }
      
      // 다중 선택 모드: 선택된 객체 중 하나를 클릭하면 그룹으로 이동
      if (isMultiSelect && hitId && selectedIds && selectedIds.has(hitId)) {
        // 선택된 객체 중 하나를 클릭: 선택 유지하고 드래그 시작
        draggingRef.current = true;
        draggingTargetIdRef.current = hitId;
        startPtRef.current = p;
        lastPtRef.current = p;
        dragStartedRef.current = false;
        dragStartClientRef.current = { x: e.clientX, y: e.clientY };
        // 다중 선택: AABB의 원본 좌표 저장
        if (getSelectedObjects) {
          const selectedObjs = getSelectedObjects();
          const aabb = computeAABB(selectedObjs);
          if (aabb) {
            initialObjectPosRef.current = {
              x: aabb.x,
              y: aabb.y
            };
          } else {
            initialObjectPosRef.current = { x: 0, y: 0 };
          }
        } else {
          initialObjectPosRef.current = { x: 0, y: 0 };
        }
        lastObjectPosRef.current = null;
        if (isDraggingRef) {
          isDraggingRef.current = true;
        }
        return;
      }
      
      // 텍스트 툴: 클릭 시 텍스트 입력 모드 활성화
      if (tool === 'text') {
        if (hitId) {
          // 텍스트 객체를 클릭한 경우: 편집 모드
          const clickedObj = getObjectById(hitId);
          if (clickedObj && clickedObj.type === 'text') {
            onTextToolClick?.(e.clientX, e.clientY, hitId);
          } else {
            // 다른 객체를 클릭한 경우: 새 텍스트 생성
            onTextToolClick?.(e.clientX, e.clientY);
          }
        } else {
          // 빈 공간 클릭: 새 텍스트 생성
          onTextToolClick?.(e.clientX, e.clientY);
        }
        return;
      }
      
      // 다중 선택 모드에서 AABB 내부 클릭: 바로 드래그 시작 (hitId와 관계없이)
      if (isMultiSelect && isInMultiSelectAABB) {
        draggingRef.current = true;
        draggingTargetIdRef.current = hitId || null;
        startPtRef.current = p;
        lastPtRef.current = p;
        dragStartedRef.current = false;
        dragStartClientRef.current = { x: e.clientX, y: e.clientY };
        // 다중 선택: AABB의 원본 좌표 저장
        if (getSelectedObjects) {
          const selectedObjs = getSelectedObjects();
          const aabb = computeAABB(selectedObjs);
          if (aabb) {
            initialObjectPosRef.current = {
              x: aabb.x,
              y: aabb.y
            };
          } else {
            initialObjectPosRef.current = { x: 0, y: 0 };
          }
        } else {
          initialObjectPosRef.current = { x: 0, y: 0 };
        }
        lastObjectPosRef.current = null;
        if (isDraggingRef) {
          isDraggingRef.current = true;
        }
        return;
      }
      
      // 객체를 클릭한 경우
      if (hitId) {
        // 다중 선택 모드에서 선택되지 않은 다른 객체 클릭 시 새 선택
        if (isMultiSelect) {
          setSelection?.([hitId]);
        } else {
          // 단일 선택 모드
          if (selectedId !== hitId) {
            setSelectedId(hitId);
          }
        }
        
        draggingRef.current = true;
        draggingTargetIdRef.current = hitId;
        startPtRef.current = p;
        lastPtRef.current = p;
        dragStartedRef.current = false;
        dragStartClientRef.current = { x: e.clientX, y: e.clientY };
        // 단일 선택: 객체의 원본 좌표 저장
        const obj = getObjectById(hitId);
        if (obj) {
          initialObjectPosRef.current = {
            x: obj.x,
            y: obj.y
          };
        } else {
          initialObjectPosRef.current = { x: 0, y: 0 };
        }
        lastObjectPosRef.current = null;
        if (isDraggingRef) {
          isDraggingRef.current = true;
        }
        return;
      }
      
      // 빈 공간 클릭: 드래그 선택 시작
      if (!hitId) {
        // 선택 해제
        if (!e.shiftKey) {
          clearSelection?.();
          setSelectedId(null);
        }
        // 드래그 선택 시작 (단일/다중 선택 모드 공통)
        dragSelectingRef.current = true;
        dragSelectStartRef.current = p;
        if (setDragSelectRect) {
          setDragSelectRect({ x: p.x, y: p.y, width: 0, height: 0 });
        }
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      const p = toWorld(e.clientX, e.clientY);
      
      // 객체 드래그 임계값 체크 (화면 좌표 기준 5px)
      if (draggingRef.current && !dragStartedRef.current && dragStartClientRef.current) {
        const dxScreen = e.clientX - dragStartClientRef.current.x;
        const dyScreen = e.clientY - dragStartClientRef.current.y;
        const threshold = 5;
        if (dxScreen * dxScreen + dyScreen * dyScreen < threshold * threshold) {
          // 아직 임계값 미만: 이동 시작하지 않음
          return;
        }
        // 임계값을 넘는 순간부터 실제 드래그 시작
        dragStartedRef.current = true;
        // 드래그 시작 시점 기준으로 마지막 포인트를 초기화
        lastPtRef.current = p;
        return;
      }
      
      // 드래그 선택 중
      if (dragSelectingRef.current && dragSelectStartRef.current && setDragSelectRect && getObjectsArray) {
        const start = dragSelectStartRef.current;
        const rect = {
          x: Math.min(start.x, p.x),
          y: Math.min(start.y, p.y),
          width: Math.abs(p.x - start.x),
          height: Math.abs(p.y - start.y)
        };
        setDragSelectRect(rect);
        
        // 드래그 선택 영역과 교차하는 객체 찾기
        const allObjects = getObjectsArray();
        const intersectingIds: string[] = [];
        
        // 사각형-원형 교차 체크 헬퍼 함수
        const rectCircleIntersects = (
          r: { x: number; y: number; width: number; height: number },
          cx: number, cy: number, rx: number, ry: number
        ): boolean => {
          // 원의 중심과 사각형의 가장 가까운 점 찾기
          const closestX = Math.max(r.x, Math.min(cx, r.x + r.width));
          const closestY = Math.max(r.y, Math.min(cy, r.y + r.height));
          
          // 원의 중심에서 가장 가까운 점까지의 거리
          const dx = cx - closestX;
          const dy = cy - closestY;
          
          // 타원의 정규화된 거리 계산
          if (rx > 0 && ry > 0) {
            const nx = dx / rx;
            const ny = dy / ry;
            return nx * nx + ny * ny <= 1;
          }
          return false;
        };
        
        allObjects.forEach(obj => {
          // 클리핑된 객체: 마스크 영역과 드래그 선택 영역이 교차하는지 확인
          if (obj.isClipped && obj.clipMaskId) {
            const maskObj = getObjectById(obj.clipMaskId);
            if (!maskObj) return;
            
            let intersectsMask = false;
            
            // 방법 1: 사각형-사각형/사각형-원형 교차 체크 (주요 방법)
            if (maskObj.type === 'rectangle' || maskObj.type === 'image' || maskObj.type === 'text') {
              // 회전이 없는 경우: 단순 AABB 교차
              if (Math.abs(maskObj.rotation || 0) < 1e-6) {
                const maskAABB = { 
                  x: maskObj.x, 
                  y: maskObj.y, 
                  width: maskObj.width, 
                  height: maskObj.height 
                };
                intersectsMask = rectIntersects(rect, maskAABB);
              } else {
                // 회전이 있는 경우: 마스크의 바운딩 박스와 교차 체크
                // 회전된 마스크의 AABB 계산
                const maskAngle = maskObj.rotation || 0;
                const mcx = maskObj.x + maskObj.width / 2;
                const mcy = maskObj.y + maskObj.height / 2;
                const corners = [
                  { x: maskObj.x, y: maskObj.y },
                  { x: maskObj.x + maskObj.width, y: maskObj.y },
                  { x: maskObj.x + maskObj.width, y: maskObj.y + maskObj.height },
                  { x: maskObj.x, y: maskObj.y + maskObj.height }
                ];
                const cos = Math.cos(maskAngle);
                const sin = Math.sin(maskAngle);
                const rotatedCorners = corners.map(p => {
                  const dx = p.x - mcx;
                  const dy = p.y - mcy;
                  return {
                    x: mcx + dx * cos - dy * sin,
                    y: mcy + dx * sin + dy * cos
                  };
                });
                const minX = Math.min(...rotatedCorners.map(c => c.x));
                const maxX = Math.max(...rotatedCorners.map(c => c.x));
                const minY = Math.min(...rotatedCorners.map(c => c.y));
                const maxY = Math.max(...rotatedCorners.map(c => c.y));
                const rotatedAABB = {
                  x: minX,
                  y: minY,
                  width: maxX - minX,
                  height: maxY - minY
                };
                intersectsMask = rectIntersects(rect, rotatedAABB);
              }
            } else if (maskObj.type === 'circle') {
              // 사각형-원형 교차 체크
              const mcx = maskObj.x + maskObj.width / 2;
              const mcy = maskObj.y + maskObj.height / 2;
              const mrx = maskObj.width / 2;
              const mry = maskObj.height / 2;
              intersectsMask = rectCircleIntersects(rect, mcx, mcy, mrx, mry);
            }
            
            // 방법 2: 점 기반 체크 (보조 방법 - 방법 1에서 놓친 경우를 보완)
            if (!intersectsMask) {
              // 드래그 선택 영역의 모서리와 중심점
              const rectPoints = [
                { x: rect.x, y: rect.y }, // 좌상단
                { x: rect.x + rect.width, y: rect.y }, // 우상단
                { x: rect.x, y: rect.y + rect.height }, // 좌하단
                { x: rect.x + rect.width, y: rect.y + rect.height }, // 우하단
                { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 } // 중심
              ];
              
              for (const point of rectPoints) {
                // 마스크 객체의 회전 고려
                const maskAngle = maskObj.rotation || 0;
                let maskTx = point.x;
                let maskTy = point.y;
                if (Math.abs(maskAngle) > 1e-6) {
                  const mcx = maskObj.x + maskObj.width / 2;
                  const mcy = maskObj.y + maskObj.height / 2;
                  const mdx = point.x - mcx;
                  const mdy = point.y - mcy;
                  const mcos = Math.cos(-maskAngle);
                  const msin = Math.sin(-maskAngle);
                  maskTx = mcx + mdx * mcos - mdy * msin;
                  maskTy = mcy + mdx * msin + mdy * mcos;
                }
                
                // 마스크 영역 체크
                let isInMask = false;
                if (maskObj.type === 'rectangle') {
                  isInMask = maskTx >= maskObj.x && maskTx <= maskObj.x + maskObj.width && 
                            maskTy >= maskObj.y && maskTy <= maskObj.y + maskObj.height;
                } else if (maskObj.type === 'circle') {
                  const mcx = maskObj.x + maskObj.width / 2;
                  const mcy = maskObj.y + maskObj.height / 2;
                  const mrx = maskObj.width / 2;
                  const mry = maskObj.height / 2;
                  if (mrx > 0 && mry > 0) {
                    const mnx = (maskTx - mcx) / mrx;
                    const mny = (maskTy - mcy) / mry;
                    isInMask = mnx * mnx + mny * mny <= 1;
                  }
                } else {
                  // image, text 등은 직사각형
                  isInMask = maskTx >= maskObj.x && maskTx <= maskObj.x + maskObj.width && 
                            maskTy >= maskObj.y && maskTy <= maskObj.y + maskObj.height;
                }
                
                if (isInMask) {
                  intersectsMask = true;
                  break;
                }
              }
            }
            
            // 마스크 영역과 교차하지 않으면 선택하지 않음
            if (!intersectsMask) return;
          }
          
          // AABB 교차 체크
          const objAABB = { x: obj.x, y: obj.y, width: obj.width, height: obj.height };
          if (rectIntersects(rect, objAABB)) {
            intersectingIds.push(obj.id);
          }
        });
        
        // Shift 키가 눌려있으면 기존 선택에 추가, 아니면 교체
        if (e.shiftKey && selectedIds) {
          // 기존 선택 유지하고 새로운 것 추가
          const newSelection = new Set([...Array.from(selectedIds), ...intersectingIds]);
          if (setSelection) {
            setSelection(Array.from(newSelection));
          }
        } else {
          // 기존 선택 교체
          if (setSelection) {
            setSelection(intersectingIds);
          }
        }
        
        requestRender();
        return;
      }
      
      // 객체 드래그 중
      if (draggingRef.current) {
        const last = lastPtRef.current;
        if (!last) { lastPtRef.current = p; return; }
        
        const initialPos = initialObjectPosRef.current;
        const startPt = startPtRef.current;
        if (!initialPos || !startPt) { lastPtRef.current = p; return; }
        
        let dx = p.x - last.x;
        let dy = p.y - last.y;
        
        // Shift 키가 눌려있으면 축스냅 적용
        if (e.shiftKey) {
          // 마우스 시작 지점과 현재 마우스 위치의 차이 계산 (드래그 시작부터 현재까지의 전체 이동량)
          const mouseDx = p.x - startPt.x;
          const mouseDy = p.y - startPt.y;
          
          // 더 큰 축 결정
          if (Math.abs(mouseDx) > Math.abs(mouseDy)) {
            // 수평 축 고정: dy = 0
            const finalDx = mouseDx;
            const finalDy = 0;
            // 원본 객체 좌표에 마우스 이동 차이를 더한 최종 위치
            const finalObjectX = initialPos.x + finalDx;
            const finalObjectY = initialPos.y + finalDy;
            
            // 이전 프레임 위치와의 차이 계산
            const lastObjPos = lastObjectPosRef.current;
            if (lastObjPos) {
              // 이전 프레임부터의 증분 이동
              dx = finalObjectX - lastObjPos.x;
              dy = finalObjectY - lastObjPos.y;
            } else {
              // Shift를 누르는 첫 프레임: 객체의 현재 실제 위치를 가져와서 계산
              let currentObjectX: number;
              let currentObjectY: number;
              
              // 다중 선택인지 확인
              const isMultiSelect = selectedIds && selectedIds.size > 1;
              if (isMultiSelect && getSelectedObjects) {
                // 다중 선택: AABB의 현재 위치
                const selectedObjs = getSelectedObjects();
                const aabb = computeAABB(selectedObjs);
                if (aabb) {
                  currentObjectX = aabb.x;
                  currentObjectY = aabb.y;
                } else {
                  currentObjectX = initialPos.x;
                  currentObjectY = initialPos.y;
                }
              } else if (selectedId && getObjectById) {
                // 단일 선택: 객체의 현재 위치
                const obj = getObjectById(selectedId);
                if (obj) {
                  currentObjectX = obj.x;
                  currentObjectY = obj.y;
                } else {
                  currentObjectX = initialPos.x;
                  currentObjectY = initialPos.y;
                }
              } else {
                currentObjectX = initialPos.x;
                currentObjectY = initialPos.y;
              }
              
              // 최종 위치와 현재 위치의 차이를 이동량으로 사용
              dx = finalObjectX - currentObjectX;
              dy = finalObjectY - currentObjectY;
            }
            lastObjectPosRef.current = { x: finalObjectX, y: finalObjectY };
          } else {
            // 수직 축 고정: dx = 0
            const finalDx = 0;
            const finalDy = mouseDy;
            // 원본 객체 좌표에 마우스 이동 차이를 더한 최종 위치
            const finalObjectX = initialPos.x + finalDx;
            const finalObjectY = initialPos.y + finalDy;
            
            // 이전 프레임 위치와의 차이 계산
            const lastObjPos = lastObjectPosRef.current;
            if (lastObjPos) {
              // 이전 프레임부터의 증분 이동
              dx = finalObjectX - lastObjPos.x;
              dy = finalObjectY - lastObjPos.y;
            } else {
              // Shift를 누르는 첫 프레임: 객체의 현재 실제 위치를 가져와서 계산
              let currentObjectX: number;
              let currentObjectY: number;
              
              // 다중 선택인지 확인
              const isMultiSelect = selectedIds && selectedIds.size > 1;
              if (isMultiSelect && getSelectedObjects) {
                // 다중 선택: AABB의 현재 위치
                const selectedObjs = getSelectedObjects();
                const aabb = computeAABB(selectedObjs);
                if (aabb) {
                  currentObjectX = aabb.x;
                  currentObjectY = aabb.y;
                } else {
                  currentObjectX = initialPos.x;
                  currentObjectY = initialPos.y;
                }
              } else if (selectedId && getObjectById) {
                // 단일 선택: 객체의 현재 위치
                const obj = getObjectById(selectedId);
                if (obj) {
                  currentObjectX = obj.x;
                  currentObjectY = obj.y;
                } else {
                  currentObjectX = initialPos.x;
                  currentObjectY = initialPos.y;
                }
              } else {
                currentObjectX = initialPos.x;
                currentObjectY = initialPos.y;
              }
              
              // 최종 위치와 현재 위치의 차이를 이동량으로 사용
              dx = finalObjectX - currentObjectX;
              dy = finalObjectY - currentObjectY;
            }
            lastObjectPosRef.current = { x: finalObjectX, y: finalObjectY };
          }
        } else {
          // Shift가 눌려있지 않으면 일반 이동
          lastObjectPosRef.current = null;
        }
        
        if (dx !== 0 || dy !== 0) {
          // 다중 선택이면 모든 선택된 객체 이동
          const targetIds = selectedIds && selectedIds.size > 0 ? Array.from(selectedIds) : undefined;
          moveSelectedBy(dx, dy, targetIds);
          lastPtRef.current = p;
          requestRender();
        }
      }
    };
    
    // AABB 교차 체크 헬퍼 함수
    const rectIntersects = (a: { x: number; y: number; width: number; height: number }, 
                            b: { x: number; y: number; width: number; height: number }): boolean => {
      return !(a.x + a.width < b.x || b.x + b.width < a.x || 
               a.y + a.height < b.y || b.y + b.height < a.y);
    };

    const onMouseUp = () => {
      const wasDragging = draggingRef.current;
      const wasDragSelecting = dragSelectingRef.current;
      const didDrag = wasDragging && dragStartedRef.current;
      
      draggingRef.current = false;
      draggingTargetIdRef.current = null;
      startPtRef.current = null;
      lastPtRef.current = null;
      initialObjectPosRef.current = null;
      lastObjectPosRef.current = null;
      dragStartedRef.current = false;
      dragStartClientRef.current = null;
      
      if (dragSelectingRef.current) {
        dragSelectingRef.current = false;
        dragSelectStartRef.current = null;
        if (setDragSelectRect) {
          setDragSelectRect(null);
        }
      }
      
      if (isDraggingRef) {
        isDraggingRef.current = false;
      }
      
      // 드래그/드래그 선택이 실제로 발생했으면 히스토리 저장
      if ((didDrag || wasDragSelecting) && onWorkComplete) {
        onWorkComplete();
      }
    };

    const onDoubleClick = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const tool = getActiveTool();
      if (tool !== 'select') return;
      if (shouldBlock && shouldBlock()) return;
      
      const p = toWorld(e.clientX, e.clientY);
      const hitId = hitTestPoint(p.x, p.y);
      
      if (hitId) {
        const clickedObj = getObjectById(hitId);
        if (clickedObj && clickedObj.type === 'text') {
          // 텍스트 객체 더블클릭: 편집 모드 활성화
          onTextToolClick?.(e.clientX, e.clientY, hitId);
        }
      }
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('dblclick', onDoubleClick);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('dblclick', onDoubleClick);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [canvasRef, getCanvasState, getActiveTool, selectedId, setSelectedId, hitTestPoint, moveSelectedBy, requestRender, getBbox, shouldBlock, getObjectById, isDraggingRef, onWorkComplete, selectedIds, addToSelection, removeFromSelection, setSelection, clearSelection, getSelectedObjects, getObjectsArray, setDragSelectRect, onTextToolClick, artboards, onArtboardClick]);
}


